package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.CMCViewer
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.Notifications
import com.siriusxm.cmc.utils.Navigator
import com.siriusxm.pia.ApplicationContext
import com.siriusxm.pia.aggregatorAdmin
import com.siriusxm.pia.components.multiSelect
import com.siriusxm.pia.components.simpleSelectNullable
import com.siriusxm.pia.rest.unifiedaggregator.RelationshipSchema
import com.siriusxm.pia.showError
import com.siriusxm.pia.views.unifiedaggregator.entities.EntityEnhancerPlugin
import com.siriusxm.pia.views.unifiedaggregator.entities.enhancer
import com.siriusxm.smithy4kt.SmithyModel
import unifiedcontent.aggregator.*
import contentingestion.unifiedmodel.EntityType
import org.jetbrains.compose.web.dom.Text
import kotlin.coroutines.cancellation.CancellationException

/**
 * Encapsulates access to the aggregator service.
 */
data class AggregatorService(
    val app: AggregatorApplication,
    val entityTypes: List<EntityTypeConfiguration> = emptyList(),
    val relationships: List<RelationshipSchema> = emptyList(),
    var producers: List<ProducerDetails> = emptyList(),
    val consumers: List<ConsumerDetails> = emptyList(),
    val partials: List<PartialSchema> = emptyList(),
    val reloader: suspend () -> Unit,
    val context: ApplicationContext,
    val entityModel: SmithyModel? = null,
) : Navigator {
    fun producerById(id: String): ProducerDetails? = producers.firstOrNull { it.id == id }
    fun consumerById(id: String): ConsumerDetails? = consumers.firstOrNull { it.id == id }
    fun entityType(type: String): EntityTypeConfiguration? = entityTypes.find { it.type == type }
    fun partial(id: String): PartialSchema? = partials.find { it.id == id }

    /**
     * The newer client API.
     */
    val clientApi: Aggregator get() = app.aggregator

    val globalNavigator: Navigator get() = context

    /**
     * Utility to get the viewer from the context.
     */
    val viewer: CMCViewer get() = context.viewer

    /**
     * The list of enhancer plugins.
     */
    val enhancers: List<EntityEnhancerPlugin> by lazy {
        context.configuration.applications?.mapNotNull {
            it.enhancer(this)
        }.orEmpty()
    }

    /**
     * Wrap the notifications configuration
     */
    val notifications: Notifications = object : Notifications {
        override fun show(notification: Notification) {
            context.notification(notification)
        }

        override fun dismiss(id: String) {
            context.dismissNotification(id)
        }
    }

    /**
     * Navigate to the path (relative to the aggregator path, so don't start with "aggregator")
     */
    override fun navigate(path: String) {
        globalNavigator.navigate("aggregator/${path.removePrefix("aggregator/")}")
    }

    override fun push(newPath: String, title: String?, render: Boolean) {
        globalNavigator.push(
            "aggregator/${newPath.removePrefix("aggregator/")}",
            title, render
        )
    }

    fun isAdmin(): Boolean {
        return viewer.aggregatorAdmin
    }

    suspend fun updateProducer(details: ProducerDetails) {
        clientApi.updateProducer(details.id, details)
        reloadProducersInternal()
    }

    suspend fun newProducer(details: ProducerDetails) {
        clientApi.addProducer(details)
        reloadProducersInternal()
    }

    suspend fun updateConsumer(details: ConsumerDetails) {
        clientApi.updateConsumer(details.id, details)
        reloadConsumersInternal()
    }

    suspend fun newConsumer(details: ConsumerDetails) {
        clientApi.addConsumer(details)
        reloadConsumersInternal()
    }

    private suspend fun reloadProducersInternal(includeDisabled: Boolean = false) {
        app.reloadProducers(includeDisabled)
    }

    private suspend fun reloadConsumersInternal(includeDisabled: Boolean = false) {
        app.reloadConsumers(includeDisabled)
    }
}

/**
 * Render a multi-select for types.
 */
@Composable
fun AggregatorService.typeSelect(
    selectedTypes: Collection<EntityTypeConfiguration>,
    maxSelected: Int = Int.MAX_VALUE,
    onChange: (Collection<EntityTypeConfiguration>) -> Unit
) {
    val sortedTypes = entityTypes.sortedAlphaBy { it.type }
    if (maxSelected == 1) {
        simpleSelectNullable(selectedTypes.firstOrNull(), sortedTypes.map {
            it.name to it
        }, "Select a type") {
            onChange(listOfNotNull(it))
        }
    } else {
        multiSelect(sortedTypes, selectedTypes, onChange) {
            Text(it.type)
        }
    }
}

@Composable
fun AggregatorService.typeSelect(
    selectedTypes: Collection<EntityType>, maxSelected: Int = Int.MAX_VALUE,
    onChange: (Collection<EntityType>) -> Unit
) {
    typeSelect(entityTypes.filter { selectedTypes.contains(it.type.toEntityType()) }, maxSelected) {
        onChange(it.map {
            EntityType.fromName(it.type)
        })
    }
}


/**
 * Wraps an API call and shows an error message if there's a failure.
 */
suspend fun AggregatorService.apiCall(failureMessage: String, block: suspend () -> Unit) {
    try {
        block()
    } catch (e: CancellationException) {
        // always re-throw cancellation.
        throw e
    } catch (t: Throwable) {
        t.printStackTrace()
        this.context.showError(failureMessage, t)
    }
}

/**
 * Get the entity type from a schema URL
 */
fun EntitySchemaURL.toEntityType(): EntityType? {
    val entityTypeId = substringAfterLast("/", "")
        .substringBefore(":").ifBlank { null }
    return entityTypeId?.let {
        EntityType.fromName(entityTypeId)
    }?.takeIf { it !is EntityType._Unknown }
}